import profileAvatar from "assets/img/avatars/avatarSimmmple.png";
import Card from "components/card";
import { FormInput } from "components/fields/FormInput";
import Dropdown from "components/dropdown";
import { useEffect, useState } from "react";
import { CopyIcon, CheckIcon } from "lucide-react";
import TooltipHorizon from "components/tooltip";
import toast from "react-hot-toast";

interface ProfileProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formData: {
    name: string;
    icon: File | null;
  };
  agentName: string;
  agentIcon: string | null;
  agentId: string;
}

const Profile = ({
  onChange,
  formData,
  agentIcon,
  agentName,
  agentId,
}: ProfileProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  // Create preview URL when icon changes
  useEffect(() => {
    if (formData.icon) {
      const url = URL.createObjectURL(formData.icon);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [formData.icon]);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      toast.success("Agent ID copied to clipboard");
      setTimeout(() => setIsCopied(false), 3000);
    }).catch(() => {
      toast.error("Failed to copy Agent ID");
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Dropdown
          button={
            <div className="relative flex h-[90px] w-[90px] cursor-pointer items-center justify-center rounded-full">
              <img
                className=" h-full w-full  rounded-full border object-cover"
                src={agentIcon || profileAvatar}
                alt={agentName}
              />
            </div>
          }
          children={
            <div className="w-48 rounded-lg bg-white p-2 shadow-lg dark:bg-navy-700">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-navy-600">
                <input
                  type="file"
                  name="icon"
                  accept="image/*"
                  className="hidden"
                  onChange={onChange}
                />
                <span>Upload Photo</span>
              </label>
              {previewUrl && (
                <button
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-navy-600"
                  onClick={() => {
                    setPreviewUrl(null);
                    onChange({ target: { name: "icon", files: null } } as any);
                  }}
                >
                  Remove Photo
                </button>
              )}
            </div>
          }
          classNames=" top-10 left-10 mt-2"
          animation="origin-top-left transition-all duration-300 ease-in-out"
        />
        <div className="w-full">
          <FormInput
            label="Name"
            placeholder="Update agent name"
            id="agentName"
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
          />
        </div>
      </div>
      
      {/* Agent ID Section */}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-700 dark:text-white">
            Agent ID
          </label>
          <div className="mt-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-navy-600 dark:bg-navy-700">
            <span className="text-sm font-mono text-gray-600 dark:text-gray-300">
              {agentId}
            </span>
          </div>
        </div>
        <TooltipHorizon
          trigger={
            <button
              type="button"
              onClick={() => copyToClipboard(agentId)}
              className="flex h-10 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-400 dark:hover:bg-navy-600 dark:hover:text-gray-300"
            >
              {isCopied ? (
                <CheckIcon className="h-4 w-4 text-green-500" />
              ) : (
                <CopyIcon className="h-4 w-4" />
              )}
            </button>
          }
          content={<span>Copy Agent ID</span>}
          placement="top"
          extra=""
        />
      </div>
    </div>
  );
};

export default Profile;
